import {
  NUBANK_UTM_MEDIUM,
  NUBANK_CAMPAIGN_NUSCORE,
  NUBANK_CAMPAIGN_LP,
  NUBANK_EXPERIENCE_SESSION_KEY,
  NUBANK_CAMPAIGN_EMAIL,
  NUBANK_CAMPAIGN_DESENROLA,
} from './nubank.constants';
import { NubankExperienceObj, NubankExperienceType, CheckNubankLoansDueDateParams} from './nubank.types';

/**
 * Extracts Nubank experience type from URL parameters.
 * Checks for specific utm_medium and utm_campaign combinations that indicate
 * the user came from a Nubank campaign.
 *
 * Note: The 'email' campaign is automatically treated as 'lp' experience.
 *
 * @returns {NubankExperienceType | null} The experience type or null if not a valid Nubank experience
 *
 * @example
 * // For URL: https://example.com/?utm_medium=nubank&utm_campaign=nuscore
 * const experience = getNubankExperienceFromURL();
 * // Returns: "nuscore"
 *
 * @example
 * // For URL: https://example.com/?utm_medium=nubank&utm_campaign=email
 * const experience = getNubankExperienceFromURL();
 * // Returns: "lp" (email campaign is treated as lp)
 */
const getNubankExperienceFromURL = (): NubankExperienceType | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const userCameFromNubank = utmMedium === NUBANK_UTM_MEDIUM;
  const userCameFromCustomCampaigns = [NUBANK_CAMPAIGN_LP, NUBANK_CAMPAIGN_NUSCORE, NUBANK_CAMPAIGN_EMAIL].includes(utmCampaign as string);

  if (!userCameFromNubank || !userCameFromCustomCampaigns) return null;

  if (utmCampaign === NUBANK_CAMPAIGN_EMAIL) return NUBANK_CAMPAIGN_LP;

  return utmCampaign as NubankExperienceType;
};

/**
 * Initializes and stores the Nubank experience in session storage.
 * Extracts the experience type from URL parameters and persists it for later use.
 *
 * Note: The 'email' campaign is automatically converted to 'lp' experience before storing.
 *
 * @example
 * // Call this function when the page loads to capture Nubank experience
 * startNubankExperience();
 */
export const startNubankExperience = () => {
  const nubankExperience = getNubankExperienceFromURL();
  if (!nubankExperience) return;
  const nubankExpObj: NubankExperienceObj = {
    experience: nubankExperience,
  };

  sessionStorage.setItem(NUBANK_EXPERIENCE_SESSION_KEY, JSON.stringify(nubankExpObj));
};

/**
 * Retrieves the current Nubank experience from URL or session storage.
 * First checks URL parameters, then falls back to session storage.
 *
 * Note: The 'email' campaign is automatically converted to 'lp' experience.
 *
 * @returns {NubankExperienceObj | null} The Nubank experience object or null if not found
 *
 * @example
 * const experience = getNubankExperience();
 * if (experience) {
 *   console.log('User experience:', experience.experience); // "nuscore" or "lp"
 * }
 *
 * @example
 * // For email campaign URL: https://example.com/?utm_medium=nubank&utm_campaign=email
 * const experience = getNubankExperience();
 * if (experience) {
 *   console.log('User experience:', experience.experience); // "lp" (email treated as lp)
 * }
 */
export const getNubankExperience = (): NubankExperienceObj | null => {
  const experienceFromUrl = getNubankExperienceFromURL();
  if (experienceFromUrl) return { experience: experienceFromUrl };

  const sessionObj = sessionStorage.getItem(NUBANK_EXPERIENCE_SESSION_KEY);
  if (sessionObj) return JSON.parse(sessionObj);

  return null;
};


export const checkNubankLoansDueDate = ({
  product,
  isNubank,
  agreementDate,
  firstInstallmentExpDate,
  shouldFormatDate,
}: CheckNubankLoansDueDateParams): boolean => {
  const shouldValidateDueDate = product === 'Empréstimo' && isNubank;
  if (!shouldValidateDueDate) return false;

  let agreementDatePlusOne;
  if (shouldFormatDate) {
    const [yearStr, monthStr, dayStr] = agreementDate.split('-');
    agreementDatePlusOne = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
  } else {
    agreementDatePlusOne = new Date(agreementDate);
  }

  agreementDatePlusOne.setDate(agreementDatePlusOne.getDate() + 1);

  const parseDDMMYYYY = (dmy: string) => {
    const [d, m, y] = dmy.split('/').map(Number);
    return new Date(y, m - 1, d);
  };

  const firstInstallmentDueDate = parseDDMMYYYY(firstInstallmentExpDate);
  const isValidD3 = firstInstallmentDueDate.getTime() > agreementDatePlusOne.getTime(); // data de vencimento > data do acordo + 1

  if (!isValidD3) return false;

  return true;
}

export const isDesenrolaCampaign = ({ creditorSlug, campaign }: { creditorSlug?: string; campaign?: string }): boolean => {
  return creditorSlug === 'nubank' && campaign === NUBANK_CAMPAIGN_DESENROLA;
};

export * from './nubank.types';
export * from './nubank.constants';
